import { CanActivateFn } from '@angular/router';
import { AuthService } from './auth.service';
import { jobCoordinatorGuard, notInOrganizationVendorGuard, notTechnicianOnlyGuard, providerOrganizationGuard } from './auth.guard';
import { roleGuard } from './role.guard';
import { CALENDAR_ROLES } from '../features/calendar/calendar-access';
import { PLAN_ROLES } from '../features/preventive-maintenance/plan-access';
import { REPORT_ROLES } from '../features/reports/report-access';

/**
 * Issue #600 - who may open a route. The one map the sidebar (ALL_NAV_ITEMS) and the route table (app.routes.ts) both read, so a page is reachable by address only for the roles
 * that see its link. The API keeps its own authorization; this only decides what the web shows and opens.
 */
export interface RouteAccess {
  /** Any match admits (AuthService.hasRole: a System Admin passes every role but SuperAdmin). Omit for a page every signed-in user may open. */
  roles?: readonly string[];
  /** Only a user of a service-provider organization (issue #614). */
  providerOrganization?: boolean;
  /** Not for a user whose only work role is the in-organization vendor role (issue #402). */
  notInOrganizationVendor?: boolean;
  /** Not for a technician-only user (issues #592 / #610): they are sent home. */
  notTechnicianOnly?: boolean;
  /** Issue #411: also admitted when the server says the user runs the provider's jobs (GET /jobs/access); the guard asks the server, so `roles` is not enforced by roleGuard here. */
  jobCoordinator?: boolean;
}

/** A route that is not behind sign-in (the guest and token pages) or that only redirects: no access rule of its own. */
export const PUBLIC_ROUTE = 'public' as const;
export const REDIRECT_ROUTE = 'redirect' as const;

const SIGNED_IN: RouteAccess = {};
const STORE_MANAGER = ['StoreInventoryManager'];
const STORE_OR_FACILITY = ['StoreInventoryManager', 'FacilityManager'];
const ADMIN_OR_FACILITY = ['SystemAdmin', 'FacilityManager'];
const ADMIN_ONLY = ['SystemAdmin'];
const SUPER_ADMIN = ['SuperAdmin'];
const ASSET_ACCESS = ['AssetManager', 'FacilityManager'];
const VENDOR_ACCESS = ['StoreInventoryManager', 'FacilityManager', 'SystemAdmin', 'SuperAdmin'];
const TASK_ACCESS = ['MaintenanceTechnician', 'VendorContractor'];
const APPROVAL_DESIGNERS = ['SystemAdmin', 'FacilityManager', 'StoreInventoryManager', 'AssetManager'];

/**
 * Keyed by the route's `path` exactly as written in app.routes.ts. A page with no link of its own (a detail or a form) has the entry of the list page it opens from.
 * app.routes.spec.ts fails for a route with no entry here.
 */
export const ROUTE_ACCESS: Readonly<Record<string, RouteAccess | typeof PUBLIC_ROUTE | typeof REDIRECT_ROUTE>> = {
  // Before sign-in, or no page of their own.
  login: PUBLIC_ROUTE,
  register: PUBLIC_ROUTE,
  'forgot-password': PUBLIC_ROUTE,
  'reset-password': PUBLIC_ROUTE,
  'accept-invitation': PUBLIC_ROUTE,
  'select-organization': PUBLIC_ROUTE,
  'sso-callback': PUBLIC_ROUTE,
  'confirm-registration': PUBLIC_ROUTE,
  'organizations/claim': PUBLIC_ROUTE,
  'service-contracts': REDIRECT_ROUTE,
  'service-contracts/:id': REDIRECT_ROUTE,
  '**': REDIRECT_ROUTE,

  // Every signed-in user (the landing page, the personal pages, the notification deep links: My Approvals stays open).
  '': SIGNED_IN,
  dashboard: { notTechnicianOnly: true },
  profile: SIGNED_IN,
  'my-approvals': SIGNED_IN,
  'my-approvals/:taskId': SIGNED_IN,
  'maintenance-requests': SIGNED_IN,
  'maintenance-requests/new': SIGNED_IN,
  'maintenance-requests/:id': SIGNED_IN,
  'work-orders': { notTechnicianOnly: true },
  'work-orders/new': SIGNED_IN,
  'work-orders/:id': SIGNED_IN,

  // Maintenance.
  calendar: { roles: CALENDAR_ROLES },
  'preventive-maintenance': { roles: PLAN_ROLES },
  'preventive-maintenance/new': { roles: PLAN_ROLES },
  'preventive-maintenance/:id/edit': { roles: PLAN_ROLES },
  'preventive-maintenance/:id': { roles: PLAN_ROLES },
  jobs: { roles: ADMIN_ONLY, jobCoordinator: true, notInOrganizationVendor: true, providerOrganization: true },
  // Unclear: no sidebar link and its parent (the inbox) is narrower than who may open a job today; the guard it had is kept (see the run log, QUESTIONS).
  'jobs/:id': { notInOrganizationVendor: true },
  'my-tasks': { roles: TASK_ACCESS, notInOrganizationVendor: true },
  'my-tasks/:id': { roles: TASK_ACCESS, notInOrganizationVendor: true },
  contracts: { roles: ADMIN_OR_FACILITY },
  'contracts/new': { roles: ADMIN_OR_FACILITY },
  'contracts/:id': { roles: ADMIN_OR_FACILITY },
  'provider-contracts': { roles: ADMIN_ONLY, providerOrganization: true },
  'provider-contracts/:id': { roles: ADMIN_ONLY, providerOrganization: true },
  reports: { roles: REPORT_ROLES },
  'reports/most-maintained-locations': { roles: REPORT_ROLES },

  // Assets and locations.
  'admin/projects': { roles: ADMIN_ONLY },
  'admin/buildings': { roles: ADMIN_OR_FACILITY },
  'admin/buildings/new': { roles: ADMIN_OR_FACILITY },
  'admin/buildings/:id': { roles: ADMIN_OR_FACILITY },
  assets: { roles: ASSET_ACCESS },
  'assets/new': { roles: ASSET_ACCESS },
  'assets/:id': { roles: ASSET_ACCESS },

  // Inventory and purchasing.
  parts: { roles: STORE_MANAGER },
  'parts/new': { roles: STORE_MANAGER },
  'parts/categories': { roles: STORE_MANAGER },
  'parts/:id': { roles: STORE_MANAGER },
  stores: { roles: STORE_OR_FACILITY },
  'stores/new': { roles: STORE_OR_FACILITY },
  'stores/:id': { roles: STORE_OR_FACILITY },
  inventory: { roles: STORE_MANAGER },
  'inventory/transactions': { roles: STORE_MANAGER },
  'inventory/adjustments': { roles: STORE_MANAGER },
  'inventory/usage-report': { roles: STORE_MANAGER },
  'purchase-orders': { roles: STORE_OR_FACILITY },
  'purchase-orders/new': { roles: STORE_OR_FACILITY },
  'purchase-orders/:id': { roles: STORE_OR_FACILITY },
  vendors: { roles: VENDOR_ACCESS },
  'vendors/new': { roles: VENDOR_ACCESS },
  'vendors/invitations/:id': { roles: VENDOR_ACCESS },
  'vendors/:id': { roles: VENDOR_ACCESS },

  // Administration.
  'admin/users': { roles: ADMIN_ONLY },
  'admin/users/new': { roles: ADMIN_ONLY },
  'admin/users/:id/transfer': { roles: ADMIN_ONLY },
  'admin/users/:id': { roles: ADMIN_ONLY },
  'admin/approval-routing': { roles: APPROVAL_DESIGNERS },
  'admin/approval-routing/:actionKey': { roles: APPROVAL_DESIGNERS },
  'admin/priority-matrix': { roles: ADMIN_OR_FACILITY },
  'admin/maintenance-categories': { roles: ADMIN_OR_FACILITY },
  'asset-categories': { roles: ['SystemAdmin', 'FacilityManager', 'AssetManager'] },
  'admin/agents': { roles: ADMIN_OR_FACILITY },
  'admin/imports': { roles: ADMIN_ONLY },
  'admin/audit-log': { roles: ADMIN_ONLY },
  'admin/notification-log': { roles: ADMIN_ONLY },
  // Organization tabs only; settingsGuard sends anyone else to /profile.
  settings: { roles: ADMIN_ONLY },

  // Platform (SuperAdmin).
  'admin/notification-templates': { roles: SUPER_ADMIN },
  'admin/notification-templates/new': { roles: SUPER_ADMIN },
  'admin/notification-templates/:id/edit': { roles: SUPER_ADMIN },
  'admin/organizations': { roles: SUPER_ADMIN },
  'admin/organizations/:id': { roles: SUPER_ADMIN },
  'admin/scopes-of-work': { roles: SUPER_ADMIN },
};

/** The access rule of a route path; throws for a path with no entry or one that has no rule (public / redirect), so a typo cannot silently open a page. */
export function accessOf(path: string): RouteAccess {
  const access = ROUTE_ACCESS[path];
  if (!access || typeof access === 'string') throw new Error(`No route access rule for "${path}" (core/route-access.ts)`);
  return access;
}

/** What the decision reads of the signed-in user: the same members the sidebar and the guards use. */
export type AccessAuth = Pick<AuthService, 'hasRole' | 'isProviderOrganization' | 'isInOrganizationVendorOnly' | 'isTechnicianOnly'>;

/** The sidebar's decision for a user other than SuperAdmin, and what the guards generated below enforce. `canRunJobs` is the server's jobs rule (JobAccessService). */
export function canAccess(access: RouteAccess, auth: AccessAuth, canRunJobs: () => boolean): boolean {
  const roleOk = !access.roles || access.roles.some((role) => auth.hasRole(role)) || (!!access.jobCoordinator && canRunJobs());
  return (
    roleOk &&
    !(access.providerOrganization && !auth.isProviderOrganization()) &&
    !(access.notInOrganizationVendor && auth.isInOrganizationVendorOnly()) &&
    !(access.notTechnicianOnly && auth.isTechnicianOnly())
  );
}

/** The guards after authGuard for a route, in a fixed order, generated from its entry. */
export function guardsFor(access: RouteAccess): CanActivateFn[] {
  return [
    ...(access.roles && !access.jobCoordinator ? [roleGuard] : []),
    ...(access.notInOrganizationVendor ? [notInOrganizationVendorGuard] : []),
    ...(access.providerOrganization ? [providerOrganizationGuard] : []),
    ...(access.notTechnicianOnly ? [notTechnicianOnlyGuard] : []),
    ...(access.jobCoordinator ? [jobCoordinatorGuard] : []),
  ];
}
